import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Alert,
  Fade,
  CircularProgress,
  Divider,
  Stack,
  Chip,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  Person as PersonIcon,
  Lock as LockIcon,
  // Email as EmailIcon,
  ArrowForward as ArrowForwardIcon,
  Shield as ShieldIcon,
  Forest as ForestIcon,
} from "@mui/icons-material";
import { keyframes } from "@mui/system";
import axios from "axios";
import { connect } from "react-redux";
import {
  showLoadingScreen,
  setSnackBar,
  resetSnackBar,
} from "../reusable_components/site_data/siteDataSlice";
import { backend_url } from "../App";
import forest_fire_new_logo from "../images/forest_fire_new_logo.jpeg";
import fireLogo from "../images/firelogo.jpeg";

// Animations
const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
  100% { transform: translateY(0px); }
`;

// const pulse = keyframes`
//   0% { opacity: 0.6; }
//   50% { opacity: 1; }
//   100% { opacity: 0.6; }
// `;

const gradientShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const Login = (props) => {
  // const history = useHistory();
  const [data, setData] = useState({ identifier: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const path = props.location?.pathname || "/login";
  const isPunjabLogin = path === "/punjablogin";
  const logo = isPunjabLogin ? forest_fire_new_logo : fireLogo;
  const logoWidth = isPunjabLogin ? "180px" : "120px";

  const handleChange = (e) => {
    setData({ ...data, [e.target.name]: e.target.value });
    if (error) setError("");
  };

  const validate = () => {
    if (!data.identifier.trim()) {
      setError("Please enter your username");
      return false;
    }
    if (!data.password) {
      setError("Please enter your password");
      return false;
    }
    if (data.password.length < 8) {
      setError("Password must be at least 8 characters");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    setError("");
    props.showLoadingScreen(true);

    try {
      const payload = {
        username: data.identifier.trim(),
        password: data.password,
      };

      const response = await axios.post(
        `${backend_url}/accounts/api/token/login/`,
        payload,
      );

      localStorage.setItem("token", response.data.auth_token);
      localStorage.setItem("user", JSON.stringify(response.data.user));
      props.setSnackBar({
        message: "Welcome back! 🎉",
        severity: "success",
      });

      const { state } = props.location;
      setTimeout(() => {
        window.location = state ? state.from.pathname : "/home";
      }, 500);
    } catch (err) {
      const errorMsg =
        err.response?.data?.non_field_errors?.[0] ||
        err.response?.data?.detail ||
        err.response?.data?.error ||
        "Invalid credentials. Please try again.";
      setError(errorMsg);
      props.setSnackBar(errorMsg);
    } finally {
      setLoading(false);
      props.showLoadingScreen(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        background:
          "linear-gradient(-45deg, #0a0a0a, #1a1a2e, #16213e, #0f3460)",
        backgroundSize: "400% 400%",
        animation: `${gradientShift} 15s ease infinite`,
        p: 2,
      }}
    >
      {/* Animated Background Orbs */}
      <Box
        sx={{
          position: "absolute",
          width: 500,
          height: 500,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(76,175,80,0.15) 0%, transparent 70%)",
          top: "-10%",
          left: "-10%",
          animation: `${float} 8s ease-in-out infinite`,
          filter: "blur(40px)",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          width: 400,
          height: 400,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(255,152,0,0.15) 0%, transparent 70%)",
          bottom: "-10%",
          right: "-5%",
          animation: `${float} 10s ease-in-out infinite`,
          animationDelay: "2s",
          filter: "blur(40px)",
        }}
      />

      {/* Grid Pattern Overlay */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          backgroundImage: `
            linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px)
          `,
          backgroundSize: "50px 50px",
          pointerEvents: "none",
        }}
      />

      {/* Main Card */}
      <Fade in timeout={800}>
        <Paper
          elevation={24}
          sx={{
            width: "100%",
            maxWidth: 440,
            borderRadius: 4,
            overflow: "hidden",
            position: "relative",
            background: "rgba(20, 20, 30, 0.85)",
            backdropFilter: "blur(30px)",
            border: "1px solid rgba(255,255,255,0.1)",
            boxShadow: "0 25px 80px rgba(0,0,0,0.6)",
            zIndex: 1,
          }}
        >
          {/* Top gradient bar */}
          <Box
            sx={{
              height: 4,
              background:
                "linear-gradient(90deg, #4caf50, #ff9800, #f44336, #4caf50)",
              backgroundSize: "300% 100%",
              animation: `${gradientShift} 3s linear infinite`,
            }}
          />

          <Box sx={{ p: { xs: 3, sm: 4 } }}>
            {/* Logo */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mb: 3,
              }}
            >
              <Box
                component="img"
                src={logo}
                alt="Logo"
                sx={{
                  width: logoWidth,
                  height: "auto",
                  maxHeight: 80,
                  objectFit: "contain",
                  filter: "drop-shadow(0 0 20px rgba(76,175,80,0.3))",
                  cursor: "pointer",
                  transition: "transform 0.3s ease",
                  "&:hover": {
                    transform: "scale(1.05)",
                  },
                }}
                onClick={() => (window.location.href = "/")}
              />
            </Box>

            {/* Title */}
            <Box sx={{ textAlign: "center", mb: 3 }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  background:
                    "linear-gradient(135deg, #ffffff 0%, #4caf50 50%, #ff9800 100%)",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  mb: 0.5,
                  letterSpacing: "-0.5px",
                }}
              >
                Welcome Back
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "rgba(255,255,255,0.5)", fontSize: "0.85rem" }}
              >
                {isPunjabLogin
                  ? "Forestry & Wildlife Punjab"
                  : "Forest Watch Monitoring System"}
              </Typography>
              <Stack
                direction="row"
                spacing={1}
                justifyContent="center"
                sx={{ mt: 1.5 }}
              >
                <Chip
                  icon={<ShieldIcon sx={{ fontSize: 14 }} />}
                  label="Secure Login"
                  size="small"
                  sx={{
                    bgcolor: "rgba(76,175,80,0.15)",
                    color: "#4caf50",
                    fontSize: "0.65rem",
                    height: 22,
                    fontWeight: 600,
                    "& .MuiChip-icon": { color: "#4caf50", fontSize: 12 },
                  }}
                />
                <Chip
                  icon={<ForestIcon sx={{ fontSize: 14 }} />}
                  label="Real-time Monitoring"
                  size="small"
                  sx={{
                    bgcolor: "rgba(255,152,0,0.15)",
                    color: "#ff9800",
                    fontSize: "0.65rem",
                    height: 22,
                    fontWeight: 600,
                    "& .MuiChip-icon": { color: "#ff9800", fontSize: 12 },
                  }}
                />
              </Stack>
            </Box>

            {/* Error Alert */}
            {error && (
              <Fade in>
                <Alert
                  severity="error"
                  sx={{
                    mb: 2,
                    borderRadius: 2,
                    bgcolor: "rgba(244,67,54,0.1)",
                    color: "#ff8a80",
                    border: "1px solid rgba(244,67,54,0.3)",
                    "& .MuiAlert-icon": { color: "#ff8a80" },
                    fontSize: "0.85rem",
                  }}
                >
                  {error}
                </Alert>
              </Fade>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit}>
              <Stack spacing={2.5}>
                {/* Identifier Field (Username or Email) */}
                <TextField
                  fullWidth
                  name="identifier"
                  label="Username"
                  placeholder="Enter Username"
                  value={data.identifier}
                  onChange={handleChange}
                  autoFocus
                  autoComplete="username"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon
                          sx={{ color: "rgba(255,255,255,0.4)", fontSize: 20 }}
                        />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      color: "#fff",
                      borderRadius: 2,
                      bgcolor: "rgba(255,255,255,0.03)",
                      "& fieldset": {
                        borderColor: "rgba(255,255,255,0.15)",
                      },
                      "&:hover fieldset": {
                        borderColor: "rgba(76,175,80,0.5)",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#4caf50",
                        borderWidth: 2,
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "rgba(255,255,255,0.5)",
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#4caf50",
                    },
                    "& .MuiInputBase-input::placeholder": {
                      color: "rgba(255,255,255,0.3)",
                      opacity: 1,
                    },
                  }}
                />

                {/* Password Field */}
                <TextField
                  fullWidth
                  name="password"
                  label="Password"
                  placeholder="Enter your password"
                  type={showPassword ? "text" : "password"}
                  value={data.password}
                  onChange={handleChange}
                  autoComplete="current-password"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon
                          sx={{ color: "rgba(255,255,255,0.4)", fontSize: 20 }}
                        />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          sx={{ color: "rgba(255,255,255,0.4)" }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      color: "#fff",
                      borderRadius: 2,
                      bgcolor: "rgba(255,255,255,0.03)",
                      "& fieldset": {
                        borderColor: "rgba(255,255,255,0.15)",
                      },
                      "&:hover fieldset": {
                        borderColor: "rgba(76,175,80,0.5)",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#4caf50",
                        borderWidth: 2,
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "rgba(255,255,255,0.5)",
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#4caf50",
                    },
                    "& .MuiInputBase-input::placeholder": {
                      color: "rgba(255,255,255,0.3)",
                      opacity: 1,
                    },
                  }}
                />

                {/* Forgot Password */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    mt: -1,
                  }}
                >
                  <Link
                    to="/reset-password"
                    style={{
                      color: "#4caf50",
                      fontSize: "0.8rem",
                      textDecoration: "none",
                      transition: "opacity 0.2s",
                    }}
                    onMouseEnter={(e) => (e.target.style.opacity = 0.7)}
                    onMouseLeave={(e) => (e.target.style.opacity = 1)}
                  >
                    Forgot password?
                  </Link>
                </Box>

                {/* Submit Button */}
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading}
                  endIcon={
                    !loading && <ArrowForwardIcon sx={{ fontSize: 18 }} />
                  }
                  sx={{
                    py: 1.5,
                    borderRadius: 2,
                    fontWeight: 700,
                    fontSize: "0.95rem",
                    letterSpacing: "0.5px",
                    textTransform: "none",
                    background:
                      "linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)",
                    boxShadow: "0 8px 24px rgba(76,175,80,0.3)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #66bb6a 0%, #388e3c 100%)",
                      boxShadow: "0 12px 32px rgba(76,175,80,0.45)",
                      transform: "translateY(-2px)",
                    },
                    "&:active": {
                      transform: "translateY(0)",
                    },
                    "&.Mui-disabled": {
                      background: "rgba(255,255,255,0.1)",
                      color: "rgba(255,255,255,0.3)",
                    },
                  }}
                >
                  {loading ? (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <CircularProgress
                        size={18}
                        sx={{ color: "rgba(255,255,255,0.7)" }}
                      />
                      Signing in...
                    </Box>
                  ) : (
                    "Sign In"
                  )}
                </Button>
              </Stack>
            </form>

            {/* Divider */}
            <Divider
              sx={{
                my: 3,
                "&::before, &::after": {
                  borderColor: "rgba(255,255,255,0.1)",
                },
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: "rgba(255,255,255,0.3)", px: 1 }}
              >
                Forest Watch System
              </Typography>
            </Divider>

            {/* Info */}
            <Box sx={{ textAlign: "center" }}>
              <Typography
                variant="caption"
                sx={{
                  color: "rgba(255,255,255,0.4)",
                  fontSize: "0.7rem",
                  display: "block",
                  lineHeight: 1.6,
                }}
              >
                Protected by advanced security protocols
                <br />© 2024 Forest Watch • All rights reserved
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Fade>
    </Box>
  );
};

const mapStateToProps = (state) => ({
  loginStatus: state.site_data,
});

export default connect(mapStateToProps, {
  showLoadingScreen,
  setSnackBar,
  resetSnackBar,
})(Login);
